import { Error } from '@mui/icons-material'
import ReceiptIcon from '@mui/icons-material/Receipt'
import { Stack, SxProps, Theme } from '@mui/material'
import React from 'react'
import {
  Br,
  Cut,
  Image,
  Line,
  Printer as RPrinter,
  Row,
  Text,
  render,
} from 'react-thermal-printer'
import { BillType, ReceiptBlock } from '../../all-types'
import { CommonBtn } from '../../components'
import { usePrinterSettings } from '../../hooks'
import { useAppSelector } from '../../redux-store/hooks'
import {
  ensureFullBillingInfo,
  getFormattedDate,
  getFormattedTime,
} from '../../utils'

const BillPrinter: React.FC<{
  bill: BillType
  btnStyles?: SxProps<Theme> | undefined
  takeawayKitchenPrint?: boolean
  iconStyles?: SxProps<Theme> | undefined
}> = ({ bill, btnStyles, iconStyles, takeawayKitchenPrint = false }) => {
  const clientInfo = useAppSelector((state) => state.clientInfo?.data)
  const { receiptProperties } = clientInfo
  const { printerUsb, printerStatus } = usePrinterSettings()
  const { timeInfo, info } = bill

  const address = `Address: ${clientInfo?.address.fullAddress}`
  const phoneNumber = `Ph: ${clientInfo?.PhoneNumber || 'N/A'}`
  const emailId = `@: ${clientInfo?.email || 'N/A'}`

  const billDate = `Date: ${getFormattedDate(Number(timeInfo.timestamp))}`
  const billTime = `Time: ${getFormattedTime(Number(timeInfo.timestamp))}`

  const billSNo = `Bill No: ${bill.sNo}`
  const tableNo = `Table No: ${info.tableNo || 'N/A'}`

  const GST = `GST: ${clientInfo?.gstNo || 'N/A'}`
  const FSSAI = `FSSAI: ${clientInfo?.fssaiNo || 'N/A'}`

  // Ensure `extraInfo` (roundOff, tax/discount display labels) is always
  // populated. Bills loaded from the backend may not have it because the
  // server schema does not persist `extraInfo`, which would otherwise cause
  // values like `roundOff` to print as `undefined`.
  const billingInfo = ensureFullBillingInfo(bill)

  const printFullBill = async () => {
    try {
      const data = await render(
        <RPrinter type="epson" characterSet="pc437_usa">
          <Image
            align="center"
            src={clientInfo.printLogo}
            style={{
              width: 100,
              height: 100,
            }}
          />
          <Text
            font={'A'}
            size={{ width: 1, height: 1 }}
            align="center"
            bold={true}
          >
            {'RESTAURANTS | BANQUET HALL'}
          </Text>
          <Br />
          <Line />
          <Text
            font={'A'}
            size={{ width: 1, height: 1 }}
            align="center"
            bold={true}
          >
            {'COMPOSITION DEALER'}
          </Text>
          <Line />
          <Text
            font={'A'}
            size={{ width: 1, height: 1 }}
            align="center"
            bold={true}
          >
            {address}
          </Text>
          <Br />
          <Row left={GST} right={FSSAI} />
          <Row left={phoneNumber} right={''} />
          <Br />
          <Text
            font={'A'}
            size={{ width: 2, height: 2 }}
            align="center"
            bold={true}
          >
            {tableNo === '999' ? 'TAKE AWAY' : 'TAX INVOICE'}
          </Text>
          <Br />
          <Line />
          <Row left={billDate} right={billTime} />
          <Row left={billSNo} right={tableNo} />
          <Line />
          <Row
            left="sno"
            center="Items"
            right="Price"
            gap={6}
            style={{ fontWeight: 600 }}
          />
          <Line />
          {bill.items.map((item, index) => (
            <Row
              key={index}
              left={`${index + 1}`}
              center={`${item.itemName} (${item.quantity + '' + item.quantityType}) x ${item.noOfItems}`}
              //center={item.quantity.toString()}
              right={`${item.billingInfo.finalAmount.toFixed(2)}`}
              gap={6}
            />
          ))}

          <Br />
          <Row
            left={''}
            right={`Sub Total: ${billingInfo.totalItemsCostRaw}`}
          />
          {Object.keys(billingInfo?.TaxChargeSheetView)?.map((taxType) => {
            const taxAmount = billingInfo?.TaxChargeSheetView?.[taxType]
            return (
              <Row key={taxType} left={''} right={`${taxType}: ${taxAmount}`} />
            )
          })}
          {billingInfo.totalTaxCharged > 0 && (
            <Row
              left={''}
              right={`Total Tax: ${billingInfo.totalTaxCharged}`}
            />
          )}
          {billingInfo.totalDiscountIssued > 0 && (
            <Row
              left={''}
              right={`Discount: ${billingInfo.totalDiscountIssued}`}
            />
          )}
          <Row
            left={''}
            right={`Bill Amount: ${billingInfo.finalBillAmount}`}
          />
          <Br />
          <Text
            font={'A'}
            size={{ width: 2, height: 2 }}
            align="center"
            bold={true}
          >
            Bill Amount RS.{billingInfo.finalBillAmount}/-
          </Text>
          <Br />
          <Line />
          <Text
            font={'A'}
            size={{ width: 1, height: 1 }}
            align="center"
            bold={true}
          >
            (COMPOSITION DEALERS NO TAX IS COLLECTED)
          </Text>
          <Line />
          <Text
            font={'A'}
            size={{ width: 1, height: 1 }}
            align="center"
            bold={true}
          >
            THANKS FOR VISITING US.
          </Text>
          <Text
            font={'A'}
            size={{ width: 1, height: 1 }}
            align="center"
            bold={true}
          >
            PLEASE COME AGAIN
          </Text>

          <Cut />
        </RPrinter>
      )
      if (printerUsb) {
        await printerUsb.print(data)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const getReceiptBlock = (block: ReceiptBlock) => {
    switch (block.type) {
      case 'text':
        return (
          <Text
            key={block.id}
            font={block.font}
            size={{
              width: block.sizeW as any,
              height: block.sizeH as any,
            }}
            align={block.align}
            bold={block.bold}
          >
            {block.content}
          </Text>
        )
      case 'image':
        return (
          <Image
            key={block.id}
            align={block.align}
            src={block.content || clientInfo.printLogo}
            style={{
              width: block.width,
              height: block.height,
            }}
          />
        )
      case 'line':
        return <Line key={block.id} />
      case 'row':
        return (
          <Row
            key={block.id}
            left={block.left || ''}
            right={block.right || ''}
          />
        )
      case 'br':
        return <Br key={block.id} />
      case 'cut':
        return <Cut key={block.id} />
      default:
        return null
    }
  }

  const printBillNew = async () => {
    try {
      const data = await render(
        <RPrinter type="epson" characterSet="pc437_usa">
          {receiptProperties?.receiptHeader?.map((block) => {
            return getReceiptBlock(block)
          })}
          {receiptProperties?.headerExtras?.map((block) => {
            return getReceiptBlock(block)
          })}
          <Br />
          <Text
            font={'A'}
            size={{ width: 2, height: 2 }}
            align="center"
            bold={true}
          >
            TAX INVOICE
          </Text>
          {info.tableNo === '999' && (
            <Text
              font={'A'}
              size={{ width: 1, height: 1 }}
              align="center"
              bold={true}
            >
              TAKE AWAY
            </Text>
          )}
          <Br />
          <Line />
          <Row left={billDate} right={billTime} />
          <Row left={billSNo} right={tableNo} />
          <Line />
          <Row
            left="sno"
            center="Items"
            right="Price"
            gap={6}
            style={{ fontWeight: 600 }}
          />
          <Line />
          {bill.items.map((item, index) => (
            <Row
              key={index}
              left={`${index + 1}`}
              center={`${item.itemName} (${item.quantity + '' + item.quantityType}) x ${item.noOfItems}`}
              //center={item.quantity.toString()}
              right={`${item.billingInfo.rawAmount.toFixed(2)}`}
              gap={6}
            />
          ))}

          <Br />
          <Row
            left={''}
            right={`Sub Total: ${billingInfo.totalItemsCostRaw}`}
          />
          {billingInfo.totalDiscountIssued > 0 && (
            <Row
              left={''}
              right={`(${billingInfo?.extraInfo?.discountName ?? ''}) Discount ${billingInfo?.extraInfo?.discountPercentage}%: -${billingInfo.totalDiscountIssued?.toFixed(2)}`}
            />
          )}

          {Object.keys(billingInfo?.TaxChargeSheetView)?.map((taxType) => {
            const taxAmount = billingInfo?.TaxChargeSheetView?.[taxType]
            const taxDisplayName = billingInfo?.extraInfo?.[taxType] ?? taxType
            return (
              <Row
                key={taxType}
                left={''}
                right={`${taxDisplayName}: ${taxAmount?.toFixed(2)}`}
              />
            )
          })}
          {/* {billingInfo.totalTaxCharged > 0 && (
            <Row
              left={''}
              right={`Total Tax: ${billingInfo.totalTaxCharged?.toFixed(2)}`}
            />
          )} */}

          {billingInfo?.extraInfo?.roundOff &&
            billingInfo.extraInfo.roundOff !== '0.00' && (
              <Row
                left={''}
                right={`Round Off: -${billingInfo.extraInfo.roundOff}`}
              />
            )}
          <Row
            left={''}
            right={`Grand Total: ${billingInfo.finalBillAmount?.toFixed(2)}`}
          />
          <Br />
          <Text
            font={'A'}
            size={{ width: 2, height: 2 }}
            align="center"
            bold={true}
          >
            Bill Amount RS.{billingInfo.finalBillAmount}/-
          </Text>
          <Br />
          {receiptProperties?.footerExtras?.map((block) => {
            return getReceiptBlock(block)
          })}
          {receiptProperties?.receiptFooter?.map((block) => {
            return getReceiptBlock(block)
          })}
        </RPrinter>
      )
      if (printerUsb) {
        await printerUsb.print(data)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const takeAwayKitchenPrint = async () => {
    try {
      const data = await render(
        <RPrinter type="epson" characterSet="pc437_usa">
          <Text
            font={'A'}
            size={{ width: 2, height: 2 }}
            align="center"
            bold={true}
          >
            {'TAKE AWAY ORDER '}#{bill.sNo}
          </Text>
          <Br />
          <Line />
          <Text
            font={'A'}
            size={{ width: 1, height: 1 }}
            align="center"
            bold={true}
          >
            {bill.takeAwayType}
          </Text>
          <Line />
          <Row
            left="sno"
            right="Items"
            //right="Price"
            gap={6}
            style={{ fontWeight: 600 }}
          />
          <Line />
          {bill.items.map((item, index) => (
            <Row
              key={index}
              left={`${index + 1}`}
              right={`${item.itemName} (${item.quantity + '' + item.quantityType}) x ${item.noOfItems}`}
              //center={item.quantity.toString()}
              //right={`${item.billingInfo.finalAmount.toFixed(2)}`}
              gap={6}
            />
          ))}
          <Br />
          <Cut />
        </RPrinter>
      )
      if (printerUsb) {
        await printerUsb.print(data)
      }
    } catch (e) {
      console.error(e)
    }
  }

  if (printerStatus && takeawayKitchenPrint) {
    return (
      <CommonBtn
        //startIcon={<ReceiptIcon sx={{ ...iconStyles }} />}
        variant="contained"
        color="primary"
        onClick={takeAwayKitchenPrint}
        sx={{ height: 60, ...btnStyles }}
      >
        <ReceiptIcon sx={{ ...iconStyles }} />
        Print
      </CommonBtn>
    )
  }

  if (printerStatus) {
    return (
      <Stack flexDirection={'row'} justifyContent={'flex-start'} gap={1}>
        {/* <CommonBtn
          //startIcon={<ReceiptIcon sx={{ ...iconStyles }} />}
          variant="contained"
          color="primary"
          onClick={printFullBill}
          sx={{ height: 60, ...btnStyles }}
        >
          <ReceiptIcon sx={{ ...iconStyles }} />
          Print
        </CommonBtn> */}
        <CommonBtn
          //startIcon={<ReceiptIcon sx={{ ...iconStyles }} />}
          variant="contained"
          color="primary"
          onClick={printBillNew}
          sx={{ height: 60, ...btnStyles }}
        >
          <ReceiptIcon sx={{ ...iconStyles }} />
          Print Receipt
        </CommonBtn>
      </Stack>
    )
  }

  return (
    <CommonBtn
      //startIcon={<Error sx={{ ...iconStyles }} />}
      variant="outlined"
      color="error"
      sx={{ height: 60, ...btnStyles }}
    >
      <Error sx={{ ...iconStyles }} />
      Error Print
    </CommonBtn>
  )
}

export { BillPrinter }
